/**
 * The single international phone-input primitive for every app (BL-1241,
 * ratifying the `PhoneInput` row of the ADR 030 § 3b Canon).
 *
 * The merge collapses two implementations of the same job — admin-web's
 * `PhoneInput` (386 lines) and bidder-web's (552 lines) — whose props already
 * matched and which drew the same DOM: a country trigger showing a flag + dial
 * code, a searchable country list, and a national-number field that emits
 * E.164. ~940 lines of near-identical parsing, formatting and validation that
 * had already drifted twice (BUG-732, BUG-796 landed on the buyer site only).
 *
 * WHY a `dialect` object rather than a pile of `className` props: ADR 030's
 * foundation is shared but its dialects are deliberately different (`--bw-*` on
 * the buyer site, admin's own palette), and `sharing.md` § 1 wants a per-app
 * skin to be a passthrough. One frozen object of class names per app keeps the
 * appearance a piece of DATA the skin declares once. Same contract as
 * `RadioButtonDialect` (BL-1243), which this deliberately mirrors.
 *
 * WHY the dropdown portals to `document.body`: the buyer site's registration
 * modal is an `overflow-hidden` scroll container that clipped an
 * absolutely-positioned list. Admin's version had the same latent bug in its own
 * modals (Add Customer, Lienholder, Profile) and simply had not been reported.
 * Positioning is structure, not appearance, so both apps get the fixed version.
 *
 * WHY the country rows are `role="option"` divs and not `<button>`s: a button is
 * not a valid child of a `role="listbox"` per the ARIA authoring practices.
 * admin-web used buttons; the merged core takes the buyer site's correct shape.
 *
 * WHY the focus ring can arrive as an inline style: static per-app colors belong
 * in the dialect's class names, where the `brandhex/no-hardcoded-brand-hex` lint
 * can see them. A tenant's brand color is only known at runtime (the buyer site
 * resolves it per auction house), so it rides `ringStyle` — the sanctioned
 * dynamic-value carve-out, and the only thing that should ever use it.
 *
 * NOT a concern of this component: the 16px minimum font size that stops iOS
 * Safari zooming (and staying zoomed) on focus. That is a bidder-web-wide CSS
 * rule keyed on `input[type="tel"]`, applied to every phone field in the app
 * automatically. It survives this merge because the core still renders
 * `type="tel"` — see the guard in the test suite.
 */
'use client';

import { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';
import type { CSSProperties, ReactNode, Ref, RefObject, ChangeEvent } from 'react';
import { ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { AsYouType, type CountryCode } from 'libphonenumber-js';
import { DEFAULT_COUNTRY, countryToEmoji, getCountryName } from './phone-countries';
import { CountryDropdown } from './phone-country-dropdown';
import type { PhoneInputDialect } from './phone-input-dialect';
import { FieldLabel } from './FormField';
import { usePhoneInput } from '../hooks/usePhoneInput';

// The dialect type is re-exported so every consumer keeps importing it from
// `@auctogon/ui/web` alongside the component — the split is an internal file
// boundary, not a change to the package's public surface.
export type { PhoneInputDialect } from './phone-input-dialect';


/**
 * Props for {@link PhoneInput}.
 *
 * Deliberately identical to the union of the two pre-merge per-app props, so
 * both skins could keep their signatures verbatim and no call site needed an
 * edit (15 importers across the two apps).
 */
export interface PhoneInputProps {
    /** E.164 phone string (e.g. "+12015550142") or empty string. */
    value: string;
    /** Called with the E.164 string on every change. Empty string when cleared. */
    onChange: (e164Value: string) => void;
    /** Default country when value is empty or has no country code. */
    defaultCountry?: CountryCode;
    /**
     * BUG-2132 — keep an extension typed after the number ("x12", "ext 12",
     * "#12"), emitted as "<E.164> ext. 12". Contact phones only; off by default.
     */
    allowExtension?: boolean;
    label?: string;
    placeholder?: string;
    /** Inline error message below the input. */
    error?: string;
    /**
     * Advisory text below the input — typically the reason it is `disabled`
     * (BL-1666). An `error` always wins: a field can be both locked and invalid,
     * and the invalid message is the one the operator can act on.
     *
     * Named to match `BidderInput`'s existing `helpText`, which already carries
     * exactly this ("Email cannot be changed"), rather than `FormField`'s
     * `disabledHint` — `PhoneInput` owns its own label and message slot, so it
     * needs the prop itself when it stands alone. Inside a `FormField` pass
     * neither `label` nor `error`; the field supplies both (BL-2238).
     */
    helpText?: string;
    required?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
    className?: string;
    id?: string;
    /**
     * Visual treatment of the input box.
     * - `'overlay'` (default) — transparent background, opacity-dimmed when
     *   disabled. For auth / registration shells and modals on a white surface.
     *   This is the look every pre-merge caller in BOTH apps got; leave it as-is.
     * - `'field'` — solid when editable, dimmed surface + full-weight border when
     *   disabled, matching a standard form `<input>` sitting beside plain inputs.
     */
    variant?: 'overlay' | 'field';
    /**
     * Inline style on the bordered row, applied ONLY while focused and not in an
     * error state. The sanctioned dynamic-value case — a tenant's runtime brand
     * color as `--tw-ring-color`. Static colors belong in the dialect.
     */
    ringStyle?: CSSProperties;
    /** Per-app appearance binding. See {@link PhoneInputDialect}. */
    dialect?: PhoneInputDialect;
    /**
     * Automation handle on the number input (BL-2197, automation-handles.md).
     * Opt-in: a form renders several phone fields, so there is no default.
     */
    testId?: string;
    /**
     * Receives the number input. Hand it React Hook Form's `field.ref` from a
     * `Controller` so a refused save can move focus here (`shouldFocusError` /
     * `setFocus`) — ADR 016 — Form Validation § 8, BL-2078.
     */
    inputRef?: (el: HTMLInputElement | null) => void;
    /**
     * Injected by a wrapping `FormField` (BL-2238): the field has an error it
     * renders itself. Paints the error state; the message stays FormField's.
     */
    invalid?: boolean;
    /**
     * Injected by a wrapping `FormField` (BL-2238): the ids of its error and
     * helper lines, put on the number input. PhoneInput's own `error` or
     * `helpText` line wins when it renders one.
     */
    'aria-describedby'?: string;
}

/**
 * Chrome state for the country dropdown — open / focus flags, the container /
 * button / anchor refs, outside-click close, mount auto-focus, and country
 * selection. Extracted from the {@link PhoneInput} body so it stays within the
 * 100-line function cap.
 * @param opts - autoFocus, the input ref to focus, and the country-selected callback
 * @returns dropdown open/focus state, refs, and handlers
 */
function usePhoneCountryDropdown(opts: {
    autoFocus: boolean;
    inputRef: RefObject<HTMLInputElement | null>;
    onCountrySelected: (code: CountryCode) => void;
}) {
    const { autoFocus, inputRef, onCountrySelected } = opts;
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    // Anchors the portaled dropdown's position + width. The dropdown renders into
    // document.body to escape any overflow-hidden / overflow-y-auto scroll
    // container (a modal) that would otherwise clip it.
    const anchorRef = useRef<HTMLDivElement>(null);

    // Auto-focus the phone input on mount when requested. `inputRef` is a stable
    // ref object and `autoFocus` is fixed per call site, so this runs once in
    // practice — spelling the deps out avoids an exhaustive-deps suppression
    // (packages/ui does not register the react-hooks plugin).
    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
    }, [autoFocus, inputRef]);

    // Close on outside click. The dropdown is portaled to document.body, so a
    // click inside it is "outside" containerRef — exclude it explicitly via the
    // data attribute. Only active while open so closed inputs add no listener.
    useEffect(() => {
        if (!dropdownOpen) return;
        function handleClickOutside(e: MouseEvent) {
            const target = e.target as Node;
            const insideContainer = containerRef.current?.contains(target);
            const insideDropdown = (target as HTMLElement | null)?.closest?.('[data-phone-country-dropdown]');
            if (!insideContainer && !insideDropdown) setDropdownOpen(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    const onCountrySelect = useCallback(
        (code: CountryCode) => {
            onCountrySelected(code);
            setDropdownOpen(false);
        },
        [onCountrySelected],
    );

    return { dropdownOpen, setDropdownOpen, isFocused, setIsFocused, containerRef, buttonRef, anchorRef, onCountrySelect };
}

/**
 * Resolve the bordered row's class list from the dialect and the current state.
 *
 * Kept out of the render body because the three state slots are MUTUALLY
 * EXCLUSIVE and that exclusivity is the contract — see {@link PhoneInputDialect}
 * `group`. Expressing it once here means a skin cannot accidentally get two
 * competing border colors.
 * @param opts - the dialect plus the row's current error / focus / disabled / variant state
 * @returns the composed class string for the row
 */
function resolveGroupClasses(opts: {
    dialect?: PhoneInputDialect;
    hasError: boolean;
    isActive: boolean;
    disabled: boolean;
    solid: boolean;
}): string {
    const { dialect, hasError, isActive, disabled, solid } = opts;
    const state = hasError ? dialect?.groupError : isActive ? dialect?.groupFocused : dialect?.groupResting;
    const surface = disabled
        ? solid
            ? dialect?.groupDisabledSolid
            : dialect?.groupDisabled
        : solid
          ? dialect?.groupSolid
          : undefined;
    return clsx('flex items-center border rounded-lg transition-colors', dialect?.group, state, surface);
}

/**
 * International phone input with a country selector and as-you-type formatting.
 * Holds the NATIONAL number in the field and emits E.164 through `onChange`.
 *
 * @param props - {@link PhoneInputProps}
 * @returns The rendered phone input.
 */
export function PhoneInput({
    value,
    onChange,
    defaultCountry = DEFAULT_COUNTRY,
    allowExtension = false,
    label,
    placeholder,
    error,
    helpText,
    required = false,
    disabled = false,
    autoFocus = false,
    className,
    id,
    variant = 'overlay',
    ringStyle,
    dialect,
    testId,
    invalid = false,
    'aria-describedby': ariaDescribedBy,
    inputRef: externalInputRef,
}: PhoneInputProps) {
    const { country, displayValue, dialCode, inputRef, handleInputChange, handleCountryChange } =
        usePhoneInput({ value, onChange, defaultCountry, allowExtension });
    // The hook's handle and the caller's (RHF's `field.ref`), on one element.
    const setInputRef = useCallback(
        (el: HTMLInputElement | null) => {
            inputRef.current = el;
            externalInputRef?.(el);
        },
        [inputRef, externalInputRef],
    );

    const solid = variant === 'field';
    const {
        dropdownOpen, setDropdownOpen, isFocused, setIsFocused,
        containerRef, buttonRef, anchorRef, onCountrySelect,
    } = usePhoneCountryDropdown({ autoFocus, inputRef, onCountrySelected: handleCountryChange });

    const reactId = useId();
    const inputId = id ?? `phone-input-${reactId}`;
    const errorId = error ? `${inputId}-error` : undefined;
    // The advisory text is described-by too when it is the only message, so a
    // screen reader announces "this can't be changed" rather than leaving the
    // disabled state unexplained.
    const helpId = !error && helpText ? `${inputId}-help` : undefined;
    const hasError = !!error || invalid;
    const dropdownId = `${inputId}-country-dropdown`;
    const isActive = isFocused || dropdownOpen;

    const defaultPlaceholder = useMemo(() => {
        if (placeholder) return placeholder;
        const formatter = new AsYouType(country);
        formatter.input('2015550100');
        return formatter.getNumber()?.formatNational() ?? '(201) 555-0100';
    }, [placeholder, country]);

    return (
        <div className={clsx('relative', className)} ref={containerRef}>
            <PhoneInputLabel htmlFor={inputId} label={label} required={required} dialect={dialect} />

            <div
                ref={anchorRef}
                className={resolveGroupClasses({ dialect, hasError, isActive, disabled, solid })}
                // Runtime tenant brand only — never a static color. Suppressed on
                // error so a brand ring can't paint over the invalid treatment.
                style={isActive && !hasError ? ringStyle : undefined}
            >
                <CountrySelectorButton
                    ref={buttonRef}
                    country={country}
                    dialCode={dialCode}
                    disabled={disabled}
                    solid={solid}
                    dropdownOpen={dropdownOpen}
                    dropdownId={dropdownId}
                    dialect={dialect}
                    onToggle={() => setDropdownOpen((prev) => !prev)}
                />

                <NationalNumberField
                    ref={setInputRef}
                    id={inputId}
                    value={displayValue}
                    onChange={handleInputChange}
                    onFocusChange={setIsFocused}
                    disabled={disabled}
                    solid={solid}
                    placeholder={defaultPlaceholder}
                    errorId={errorId ?? helpId ?? ariaDescribedBy}
                    hasError={hasError}
                    dialect={dialect}
                    testId={testId}
                />
            </div>

            {error ? (
                <p id={errorId} className={dialect?.errorText}>
                    {error}
                </p>
            ) : helpText ? (
                <p id={helpId} className={dialect?.helpText ?? 'mt-1 text-xs text-gray-500'}>
                    {helpText}
                </p>
            ) : null}

            {dropdownOpen && (
                <CountryDropdown
                    id={dropdownId}
                    country={country}
                    dialect={dialect}
                    onSelect={onCountrySelect}
                    onClose={() => setDropdownOpen(false)}
                    buttonRef={buttonRef}
                    anchorRef={anchorRef}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-components — tightly coupled to PhoneInput, kept in the same file (as
// both pre-merge implementations did).
// ---------------------------------------------------------------------------

/**
 * The field's label plus its required indicator. Renders nothing when the
 * caller passes no label. Extracted from {@link PhoneInput}'s body to keep that
 * function under the 100-line cap.
 * @param props - the label text, required flag, target id, and dialect
 * @returns The rendered label, or null.
 */
function PhoneInputLabel({
    htmlFor,
    label,
    required,
    dialect,
}: {
    htmlFor: string;
    label?: string;
    required: boolean;
    dialect?: PhoneInputDialect;
}): ReactNode {
    // BL-1087: delegates to the shared FieldLabel so the marker has one
    // implementation; the per-app dialect still supplies the classes.
    return (
        <FieldLabel
            label={label}
            htmlFor={htmlFor}
            required={required}
            className={dialect?.label}
            markClassName={dialect?.requiredMark}
        />
    );
}

// ---------------------------------------------------------------------------

interface NationalNumberFieldProps {
    id: string;
    /** `data-testid` on the input; absent when the caller names none. */
    testId?: string;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocusChange: (focused: boolean) => void;
    disabled: boolean;
    /** True under the `field` variant, which has its own disabled text treatment. */
    solid: boolean;
    placeholder: string;
    /** id of the inline error paragraph, or undefined when there is no error. */
    errorId: string | undefined;
    hasError: boolean;
    dialect?: PhoneInputDialect;
    ref?: Ref<HTMLInputElement>;
}

/**
 * The national-number text field.
 *
 * Extracted from {@link PhoneInput}'s body purely to keep that function under
 * the 100-line cap — it owns no state of its own.
 *
 * `type="tel"` is load-bearing beyond the mobile keyboard: bidder-web's app-wide
 * 16px font-size rule (the iOS-zoom floor, BUG-747) selects on it.
 * @param props - {@link NationalNumberFieldProps}
 * @returns The rendered input.
 */
function NationalNumberField({
    id, testId, value, onChange, onFocusChange, disabled, solid, placeholder, errorId, hasError, dialect, ref,
}: NationalNumberFieldProps) {
    return (
        <input
            ref={ref}
            id={id}
            data-testid={testId}
            type="tel"
            value={value}
            onChange={onChange}
            onFocus={() => onFocusChange(true)}
            onBlur={() => onFocusChange(false)}
            disabled={disabled}
            placeholder={placeholder}
            className={clsx(
                'flex-1 min-w-0 px-3 py-2 bg-transparent rounded-r-lg focus:outline-none',
                dialect?.inputText ?? 'text-sm',
                solid && disabled ? dialect?.inputDisabled : dialect?.input,
                disabled && 'cursor-not-allowed',
            )}
            aria-describedby={errorId}
            aria-invalid={hasError}
        />
    );
}

// ---------------------------------------------------------------------------

interface CountrySelectorButtonProps {
    country: CountryCode;
    dialCode: string;
    disabled: boolean;
    /** In the solid `field` variant the row drops opacity, so the trigger dims here. */
    solid: boolean;
    dropdownOpen: boolean;
    /** id of the portaled listbox this button controls (for aria-controls). */
    dropdownId: string;
    dialect?: PhoneInputDialect;
    onToggle: () => void;
    ref?: RefObject<HTMLButtonElement | null>;
}

/** Button showing the selected country flag + dial code; toggles the dropdown. */
function CountrySelectorButton({
    country, dialCode, disabled, solid, dropdownOpen, dropdownId, dialect, onToggle, ref,
}: CountrySelectorButtonProps) {
    return (
        <button
            ref={ref}
            type="button"
            disabled={disabled}
            onClick={() => { if (!disabled) onToggle(); }}
            className={clsx(
                'flex items-center gap-1 px-3 py-2 border-r rounded-l-lg text-sm focus:outline-none flex-shrink-0',
                dialect?.trigger,
                disabled ? 'pointer-events-none' : dialect?.triggerHover,
                disabled && solid && dialect?.triggerDisabledSolid,
            )}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            // Only while open — a reference to an absent id is worse than none.
            aria-controls={dropdownOpen ? dropdownId : undefined}
            // The country NAME, not just the code: ~20 countries share "+1".
            aria-label={`Selected country: ${getCountryName(country)} (${dialCode})`}
        >
            <span className="text-base leading-none">{countryToEmoji(country)}</span>
            <span className={clsx('font-medium', dialect?.triggerDial)}>{dialCode}</span>
            <ChevronDown className={clsx('w-3 h-3 transition-transform', dialect?.triggerChevron, dropdownOpen && 'rotate-180')} />
        </button>
    );
}

// ---------------------------------------------------------------------------
